/**
 * MOTOR DO RATEIO: tipos e regras de leitura para a tela de operação (Fase 1 do
 * front). Contrato do backend: #303 (dry-run), #306 (ligar), #310 (estado).
 *
 * ⛔ A tela NÃO roda o dry-run ao abrir: lê `GET /clients/:id/rateio/motor`.
 * Simular é ato do operador, porque carimba a prova (`dryRunEm`).
 *
 * ⛔ `faturas[]` do dry-run é por CONTRATO ativo, não por empresa. Hoje cada empresa
 * tem um, mas a chave de lista é `contractId`; `clientId` só agrupa para somar.
 */

export type MotivoDeNaoAplicar =
  | 'grupo-sem-motor'
  | 'sem-versao-na-competencia'
  | 'empresa-sem-cota'
  | 'sem-valor-acordado'
  | 'sem-quebra-por-natureza'
  | 'natureza-sem-produto'
  | 'quebra-nao-soma'
  | 'balanco-nao-fecha';

export interface ContratoDoDryRun {
  clientId: number;
  nome: string;
  contractId: number;
  limpeza: {
    renomeados: Array<{ productId: number; de: string; para: string }>;
    removidos: number;
    mrrNaoSeMove: boolean;
    somaDosItens: number;
  };
  aplicaria: boolean;
  motivo: MotivoDeNaoAplicar | null;
  detalhe: string | null;
  antes: number;
  depois: number;
  deltaCentavos: number;
  suprimidos: Array<{ productId: number; name: string; totalValue: number }>;
  linhasDeCota: Array<{ natureza: string; productCode: string; totalValue: number; description: string }>;
}

export interface ResumoDoDryRun {
  competencia: string;
  matrizId: number;
  versaoId: number | null;
  todasAplicariam: boolean;
  mudariaTotal: boolean;
  limpezaMoveriaMrr: boolean;
  renomeacoes: number;
  faturas: ContratoDoDryRun[];
  rodadoPor: string | null;
}

export interface MotorLigado {
  ativo: boolean;
  ativoEm?: string | null;
  ativoPor?: number | null;
  ativoPorNome?: string | null;
}

/** `GET /clients/:id/rateio/motor` (ADMIN). */
export type EstadoDoMotor =
  | { matrizId: number; temRateio: false; motivo: string; motor: MotorLigado }
  | {
      matrizId: number;
      temRateio: true;
      motor: MotorLigado;
      dryRun: {
        em: string | null;
        /** null = NUNCA rodou · false = rodou e vale · true = pede simulação nova. */
        vencido: boolean | null;
        podeLigar: boolean;
        resumo: ResumoDoDryRun | null;
      };
    };

export type SituacaoDaSimulacao = 'nunca' | 'valida' | 'vencida';

/** Os três estados de `vencido`. `null` NÃO é "vencida": é "nunca rodou". */
export function situacaoDaSimulacao(vencido: boolean | null | undefined): SituacaoDaSimulacao {
  if (vencido === true) return 'vencida';
  if (vencido === false) return 'valida';
  return 'nunca';
}

/** O porquê de uma empresa não aplicar, em texto de gente. */
export const TEXTO_DO_MOTIVO: Record<MotivoDeNaoAplicar, string> = {
  'grupo-sem-motor': 'O grupo não tem rateio ativo para esta empresa.',
  'sem-versao-na-competencia': 'Não havia rateio nesta competência. O motor não inventa uma divisão.',
  'empresa-sem-cota': 'A empresa não tem fatia na versão vigente.',
  'sem-valor-acordado': 'A versão desta competência não tem a cota em reais. O motor não calcula pelo percentual.',
  'sem-quebra-por-natureza': 'Falta separar a cota entre serviço e telecom: a nota sairia com imposto errado.',
  'natureza-sem-produto': 'Falta o produto RATEIO-NFSE ou RATEIO-NFCOM no catálogo.',
  'quebra-nao-soma': 'A separação entre serviço e telecom não soma a cota da empresa.',
  'balanco-nao-fecha': 'O que sairia da fatura não é igual ao que entraria: algum item do pool escapou, ou o valor do item deixou de ser a cota.',
};

const centavos = (v: number) => Math.round(Number(v || 0) * 100);

export interface EmpresaDoDryRun {
  clientId: number;
  nome: string;
  /** Um ou mais contratos ativos da empresa, na ordem da resposta. */
  contratos: ContratoDoDryRun[];
  aplicaria: boolean;
  antesCentavos: number;
  depoisCentavos: number;
  deltaCentavos: number;
  suprimidoCentavos: number;
  emitidoCentavos: number;
}

/**
 * Agrupa os contratos por empresa para SOMAR, sem perder nem duplicar contrato:
 * empresa com dois contratos ativos vira uma empresa com os dois dentro. A ordem é
 * a da primeira aparição de cada empresa. Somas em centavos inteiros.
 */
export function empresasDoDryRun(faturas: ContratoDoDryRun[]): EmpresaDoDryRun[] {
  const porEmpresa = new Map<number, EmpresaDoDryRun>();
  for (const f of faturas || []) {
    let e = porEmpresa.get(f.clientId);
    if (!e) {
      e = { clientId: f.clientId, nome: f.nome, contratos: [], aplicaria: true, antesCentavos: 0, depoisCentavos: 0, deltaCentavos: 0, suprimidoCentavos: 0, emitidoCentavos: 0 };
      porEmpresa.set(f.clientId, e);
    }
    e.contratos.push(f);
    e.aplicaria = e.aplicaria && f.aplicaria;
    e.antesCentavos += centavos(f.antes);
    e.depoisCentavos += centavos(f.depois);
    e.deltaCentavos += f.deltaCentavos || 0;
    e.suprimidoCentavos += (f.suprimidos || []).reduce((s, i) => s + centavos(i.totalValue), 0);
    e.emitidoCentavos += (f.linhasDeCota || []).reduce((s, l) => s + centavos(l.totalValue), 0);
  }
  return Array.from(porEmpresa.values());
}

export type CodigoDoLigar = 'dry_run_ausente' | 'dry_run_vencido' | 'dry_run_com_recusa' | 'limpeza_moveria_valor';

export interface RecusaDoLigar {
  erro: CodigoDoLigar;
  titulo: string;
  texto: string;
  /** limpeza_moveria_valor é o único `danger`: é o "pare". */
  tom: 'warning' | 'danger';
  /** Ação sugerida na tela: simular de novo, ou nenhuma (pare). */
  acao: 'simular' | 'ver-simulacao' | null;
  empresas?: Array<{ clientId: number; nome: string; motivo: MotivoDeNaoAplicar | null; detalhe: string | null }>;
  contractId?: number;
}

/** O 409 do Ligar em texto humano; null quando o erro não é um deles. */
export function lerRecusaDoLigar(err: any): RecusaDoLigar | null {
  const r = err?.response;
  if (r?.status !== 409 || !r.data || typeof r.data !== 'object') return null;
  const d = r.data;
  switch (d.erro) {
    case 'dry_run_ausente':
      return { erro: d.erro, titulo: 'Falta simular', texto: 'Este grupo nunca foi simulado. Simule a competência e confira empresa a empresa antes de ligar.', tom: 'warning', acao: 'simular' };
    case 'dry_run_vencido':
      return { erro: d.erro, titulo: 'A simulação ficou velha', texto: 'Algo mudou depois da simulação: contrato, item, nome de item ou versão do rateio. O que foi conferido não existe mais. Simule de novo e confira de novo.', tom: 'warning', acao: 'simular' };
    case 'dry_run_com_recusa':
      return { erro: d.erro, titulo: 'Alguma empresa não aplicaria', texto: 'Ligar assim deixaria parte do grupo no mecanismo antigo.', tom: 'warning', acao: 'ver-simulacao', empresas: Array.isArray(d.empresas) ? d.empresas : [] };
    case 'limpeza_moveria_valor':
      return { erro: d.erro, titulo: 'Pare: a limpeza mudaria valor', texto: `Tirar o "(rateio X%)" do nome mudaria a soma dos itens${d.contractId ? ` do contrato ${d.contractId}` : ''}. Isso indica que o item compartilhado voltou ao preço de lista na matriz. Nada foi gravado. Não tente de novo: chame quem cuida do rateio.`, tom: 'danger', acao: null, contractId: d.contractId };
    default:
      return null;
  }
}

export type CriterioDeRateio = 'ramais_contratados' | 'percentual_fixo' | 'partes_iguais';
export type BaseDoPercentual = 'pool_compartilhado' | 'total_do_grupo';

export interface FatiaDeclarada {
  clientId: number;
  percentual: number;
  valorVigente: number;
  valorVigentePorNatureza?: Record<string, number> | null;
}

/** Corpo de `POST /clients/:id/rateio/competencia-passada`. */
export interface DeclaracaoDeCompetenciaPassada {
  competenciaInicio: string;
  criterio: CriterioDeRateio;
  produtoIds: number[];
  produtoIdsBase?: number[];
  fatias: FatiaDeclarada[];
  poolDaCompetencia: number;
  observacao: string;
  baseDoPercentual?: BaseDoPercentual;
  descontosSeguemRateio?: boolean;
  recalcularCotas?: boolean;
}

export interface ProblemaDaDeclaracao {
  campo: string;
  problema: string;
}

/** O 400 `declaracao_invalida`, com TODOS os problemas de uma vez; senão, null. */
export function lerProblemasDaDeclaracao(err: any): ProblemaDaDeclaracao[] | null {
  const r = err?.response;
  if (r?.status !== 400) return null;
  const d = r.data;
  if (!d || d.erro !== 'declaracao_invalida' || !Array.isArray(d.problemas)) return null;
  return d.problemas as ProblemaDaDeclaracao[];
}
